import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import marketDataService from '../services/marketDataService';
import {
  ArrowUpIcon,
  ArrowDownIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  StarIcon,
  ChartBarIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';

// Import professional images
import tradingFloorImage from '../assets/traders-profit-279315.jpg';
import marketAnalysisImage from '../assets/tablet-business-overlay.jpg';

const Markets = () => {
  const [activeTab, setActiveTab] = useState('stocks');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('market_cap');
  const [watchlist, setWatchlist] = useState(['AAPL', 'BTC', 'EURUSD']);
  const [liveStockData, setLiveStockData] = useState([]);
  const [liveCryptoData, setLiveCryptoData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch live market data
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [stockData, cryptoData] = await Promise.all([
          marketDataService.getStockPrices(),
          marketDataService.getCryptoPrices()
        ]);
        
        // Format stock data to match the expected structure
        const formattedStockData = stockData.map(stock => ({
          symbol: stock.symbol,
          name: stock.name,
          price: parseFloat(stock.price),
          change: parseFloat(stock.change),
          changePercent: parseFloat(stock.change),
          volume: stock.volume,
          marketCap: stock.symbol === 'AAPL' ? '2.8T' : stock.symbol === 'GOOGL' ? '1.7T' : stock.symbol === 'MSFT' ? '2.6T' : stock.symbol === 'TSLA' ? '740B' : '1.6T',
          sector: stock.symbol === 'AAPL' || stock.symbol === 'GOOGL' || stock.symbol === 'MSFT' ? 'Technology' : stock.symbol === 'TSLA' ? 'Automotive' : 'Other'
        }));

        // Format crypto data to match the expected structure
        const formattedCryptoData = liveCryptoData.map(crypto => ({
          symbol: crypto.symbol,
          name: crypto.name,
          price: crypto.price,
          change: crypto.change,
          changePercent: crypto.change,
          volume: marketDataService.formatNumber(crypto.volume),
          marketCap: crypto.symbol === 'BTC' ? '895B' : crypto.symbol === 'ETH' ? '388B' : crypto.symbol === 'SOL' ? '42.1B' : crypto.symbol === 'ADA' ? '16.2B' : '10B',
          category: crypto.symbol === 'BTC' ? 'Currency' : 'Platform'
        }));

        setLiveStockData(formattedStockData);
        setLiveCryptoData(cryptoData);
      } catch (error) {
        console.error('Error fetching market data:', error);
        // Keep using static data as fallback
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
    
    // Update every 30 seconds
    const interval = setInterval(fetchData, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const marketTabs = [
    { id: 'stocks', name: 'Stocks', icon: ChartBarIcon },
    { id: 'crypto', name: 'Cryptocurrency', icon: GlobeAltIcon },
    { id: 'forex', name: 'Forex', icon: GlobeAltIcon },
    { id: 'commodities', name: 'Commodities', icon: ChartBarIcon },
  ];

  const stockData = [
    {
      symbol: 'AAPL',
      name: 'Apple Inc.',
      price: 178.45,
      change: 2.34,
      changePercent: 1.33,
      volume: '45.2M',
      marketCap: '2.8T',
      sector: 'Technology'
    },
    {
      symbol: 'GOOGL',
      name: 'Alphabet Inc.',
      price: 2734.56,
      change: -12.45,
      changePercent: -0.45,
      volume: '1.2M',
      marketCap: '1.7T',
      sector: 'Technology'
    },
    {
      symbol: 'TSLA',
      name: 'Tesla Inc.',
      price: 234.67,
      change: 8.90,
      changePercent: 3.95,
      volume: '23.1M',
      marketCap: '740B',
      sector: 'Automotive'
    },
    {
      symbol: 'MSFT',
      name: 'Microsoft Corp.',
      price: 345.78,
      change: 5.67,
      changePercent: 1.67,
      volume: '18.4M',
      marketCap: '2.6T',
      sector: 'Technology'
    },
    {
      symbol: 'AMZN',
      name: 'Amazon.com Inc.',
      price: 156.89,
      change: -2.34,
      changePercent: -1.47,
      volume: '32.1M',
      marketCap: '1.6T',
      sector: 'E-commerce'
    },
  ];

  const cryptoData = [
    {
      symbol: 'BTC',
      name: 'Bitcoin',
      price: 45678.90,
      change: -1234.56,
      changePercent: -2.63,
      volume: '28.5B',
      marketCap: '895B',
      category: 'Currency'
    },
    {
      symbol: 'ETH',
      name: 'Ethereum',
      price: 3234.56,
      change: 123.45,
      changePercent: 3.97,
      volume: '12.3B',
      marketCap: '388B',
      category: 'Platform'
    },
    {
      symbol: 'BNB',
      name: 'Binance Coin',
      price: 456.78,
      change: 12.34,
      changePercent: 2.78,
      volume: '1.2B',
      marketCap: '68.4B',
      category: 'Exchange'
    },
    {
      symbol: 'SOL',
      name: 'Solana',
      price: 98.76,
      change: -3.45,
      changePercent: -3.38,
      volume: '892M',
      marketCap: '42.1B',
      category: 'Platform'
    },
    {
      symbol: 'ADA',
      name: 'Cardano',
      price: 0.4567,
      change: 0.0234,
      changePercent: 5.40,
      volume: '456M',
      marketCap: '16.2B',
      category: 'Platform'
    },
  ];

  const forexData = [
    {
      symbol: 'EURUSD',
      name: 'Euro / US Dollar',
      price: 1.0856,
      change: 0.0023,
      changePercent: 0.21,
      volume: '156B',
      category: 'Major'
    },
    {
      symbol: 'GBPUSD',
      name: 'British Pound / US Dollar',
      price: 1.2634,
      change: -0.0045,
      changePercent: -0.35,
      volume: '89B',
      category: 'Major'
    },
    {
      symbol: 'USDJPY',
      name: 'US Dollar / Japanese Yen',
      price: 149.87,
      change: 0.45,
      changePercent: 0.30,
      volume: '134B',
      category: 'Major'
    },
    {
      symbol: 'AUDUSD',
      name: 'Australian Dollar / US Dollar',
      price: 0.6543,
      change: 0.0012,
      changePercent: 0.18,
      volume: '45B',
      category: 'Major'
    },
  ];

  const commoditiesData = [
    {
      symbol: 'GOLD',
      name: 'Gold',
      price: 1987.45,
      change: 12.34,
      changePercent: 0.62,
      volume: '156M',
      category: 'Precious Metals'
    },
    {
      symbol: 'SILVER',
      name: 'Silver',
      price: 23.45,
      change: -0.23,
      changePercent: -0.97,
      volume: '89M',
      category: 'Precious Metals'
    },
    {
      symbol: 'OIL',
      name: 'Crude Oil WTI',
      price: 87.65,
      change: 1.23,
      changePercent: 1.42,
      volume: '234M',
      category: 'Energy'
    },
    {
      symbol: 'NATGAS',
      name: 'Natural Gas',
      price: 2.84,
      change: -0.05,
      changePercent: -1.73,
      volume: '123M',
      category: 'Energy'
    },
  ];

  const getCurrentData = () => {
    switch (activeTab) {
      case 'stocks':
        return liveStockData.length > 0 ? liveStockData : stockData;
      case 'crypto':
        return liveCryptoData.length > 0 ? liveCryptoData : cryptoData;
      case 'forex':
        return forexData;
      case 'commodities':
        return commoditiesData;
      default:
        return liveStockData.length > 0 ? liveStockData : stockData;
    }
  };

  const filteredData = getCurrentData().filter(item =>
    item.symbol.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleWatchlist = (symbol) => {
    setWatchlist(prev =>
      prev.includes(symbol)
        ? prev.filter(s => s !== symbol)
        : [...prev, symbol]
    );
  };

  const formatPrice = (price) => {
    if (activeTab === 'crypto' && price < 1) {
      return price.toFixed(4);
    }
    return price.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 w-full overflow-x-hidden">
      {/* Professional Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-slate-900 via-blue-900 to-black overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src={marketAnalysisImage} 
            alt="Market Analysis" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900/80 via-blue-900/80 to-black/80"></div>
        </div>
        
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Global Markets
              <span className="block text-transparent bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text">
                Real-Time Data
              </span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed mb-8">
              Access live market data for stocks, cryptocurrencies, forex, and commodities. 
              Make informed trading decisions with professional-grade analytics.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-300">
              <div className="flex items-center">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                Live Market Data
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                10,000+ Assets
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 bg-purple-500 rounded-full mr-2"></div>
                Real-Time Updates
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-2 sm:px-4 lg:px-8 w-full -mt-10 relative z-10">

        {/* Market Tabs */}
        <div className="mb-8">
          <div className="bg-white rounded-2xl shadow-lg p-2 border border-gray-100">
            <nav className="flex space-x-1">
              {marketTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center px-6 py-3 rounded-xl font-medium text-sm transition-all duration-300 ${
                    activeTab === tab.id
                      ? 'bg-blue-600 text-white shadow-lg transform scale-105'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <tab.icon className="h-5 w-5 mr-2" />
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="mb-8 bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder={`Search ${activeTab}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300"
              />
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 transition-all duration-300"
              >
                <option value="market_cap">Market Cap</option>
                <option value="price">Price</option>
                <option value="change">Change</option>
                <option value="volume">Volume</option>
              </select>
              <button className="flex items-center justify-center px-6 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-xl hover:bg-gray-100 transition-all duration-300">
                <FunnelIcon className="h-4 w-4 mr-2" />
                Filter
              </button>
            </div>
          </div>
        </div>

        {/* Market Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden w-full">
          {isLoading && (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Loading live market data...</span>
            </div>
          )}
          
          {!isLoading && (
            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Asset
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    24h Change
                  </th>
                  <th className="hidden sm:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Volume
                  </th>
                  {(activeTab === 'stocks' || activeTab === 'crypto') && (
                    <th className="hidden md:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Market Cap
                    </th>
                  )}
                  <th className="hidden lg:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredData.map((item, index) => (
                  <motion.tr
                    key={item.symbol}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className="hover:bg-gray-50"
                  >
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <button
                          onClick={() => toggleWatchlist(item.symbol)}
                          className="mr-2 sm:mr-3 text-gray-400 hover:text-yellow-500"
                        >
                          {watchlist.includes(item.symbol) ? (
                            <StarIconSolid className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-500" />
                          ) : (
                            <StarIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                          )}
                        </button>
                        <div>
                          <div className="text-xs sm:text-sm font-medium text-gray-900">
                            {item.symbol}
                          </div>
                          <div className="text-xs sm:text-sm text-gray-500 truncate max-w-24 sm:max-w-48">
                            {item.name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                      <div className="text-xs sm:text-sm font-medium text-gray-900">
                        ${formatPrice(item.price)}
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                      <div className={`flex items-center text-xs sm:text-sm ${
                        item.change >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {item.change >= 0 ? (
                          <ArrowUpIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                        ) : (
                          <ArrowDownIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                        )}
                        <span className="font-medium">
                          {item.changePercent >= 0 ? '+' : ''}
                          {item.changePercent.toFixed(2)}%
                        </span>
                        <span className="hidden sm:inline ml-2 text-gray-500">
                          ({item.change >= 0 ? '+' : ''}
                          {formatPrice(Math.abs(item.change))})
                        </span>
                      </div>
                    </td>
                    <td className="hidden sm:table-cell px-3 sm:px-6 py-4 whitespace-nowrap">
                      <div className="text-xs sm:text-sm text-gray-900">{item.volume}</div>
                    </td>
                    {(activeTab === 'stocks' || activeTab === 'crypto') && (
                      <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap">
                        <div className="text-xs sm:text-sm text-gray-900">{item.marketCap}</div>
                      </td>
                    )}
                    <td className="hidden lg:table-cell px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                        {item.sector || item.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex space-x-2">
                        <button className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-xs">
                          Buy
                        </button>
                        <button className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-xs">
                          Sell
                        </button>
                        <button className="text-primary-600 hover:text-primary-900 text-xs">
                          Chart
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>

        {/* Market Summary Cards */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Market Status</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">US Markets:</span>
                <span className="text-sm font-medium text-green-600">Open</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Crypto:</span>
                <span className="text-sm font-medium text-green-600">24/7</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Forex:</span>
                <span className="text-sm font-medium text-green-600">Active</span>
              </div>
            </div>
          </div>

          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Top Gainers</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">TSLA</span>
                <span className="text-sm font-medium text-green-600">+3.95%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">ETH</span>
                <span className="text-sm font-medium text-green-600">+3.97%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">ADA</span>
                <span className="text-sm font-medium text-green-600">+5.40%</span>
              </div>
            </div>
          </div>

          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Top Losers</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">BTC</span>
                <span className="text-sm font-medium text-red-600">-2.63%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">SOL</span>
                <span className="text-sm font-medium text-red-600">-3.38%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">AMZN</span>
                <span className="text-sm font-medium text-red-600">-1.47%</span>
              </div>
            </div>
          </div>

          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Market Indices</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">S&P 500</span>
                <span className="text-sm font-medium text-green-600">+0.85%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">NASDAQ</span>
                <span className="text-sm font-medium text-green-600">+1.12%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">DJI</span>
                <span className="text-sm font-medium text-green-600">+0.67%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Markets; 